// Entry-bundle half of the checkout return params: takes purchase references
// (Stripe Checkout Session, PaymentIntent and PayPal order ids) out of the
// address bar and keeps them for the page (src/utils/funnelReturnParams.ts).
// Keep this file tiny: it ships in the entry chunk.
//
// Why: for 24 hours a purchase reference in the return URL names the one-time
// purchase the public one-click upsell and downsell endpoints work from, and
// pixel and analytics SDKs read the page URL on their own (ttq.page(), fbq
// PageView, gtag).
import { storeFunnelReturnParams, validCheckoutSessionId } from './funnelReturnStore';

// Removed and kept: a page reads them back through parseFunnelReturnRefs.
const KEPT_PARAMS = new Set(['payment_intent', 'checkout_session_id', 'session_id', 'paypal_order_id', 'token']);
// Stay in the URL; a copy is kept so a page that lost them still has context.
const CONTEXT_PARAMS = ['provider', 'checkout'] as const;

function isTaken(name: string, value: string, payPalReturn: boolean): boolean {
  switch (name) {
    case 'checkout_session_id':
    case 'session_id':
      // Other session_id values (UUIDs, for example) belong to other features.
      return Boolean(validCheckoutSessionId(value));
    case 'payment_intent':
      return value.startsWith('pi_');
    case 'payment_intent_client_secret':
    case 'redirect_status':
    case 'paypal_order_id':
      return true;
    case 'token':
    case 'PayerID':
      // Auth verify, password reset, unsubscribe and magic links all read
      // ?token=, so it only leaves the URL on a PayPal return.
      return payPalReturn;
    default:
      return false;
  }
}

/**
 * Removes the purchase references from `search` (in place). `kept` is what a
 * page can read back: the references plus the `provider` and `checkout`
 * context. The client secret, redirect status and PayerID leave the URL but
 * are not kept: nothing reads them.
 */
export function takeCheckoutReturnRefs(search: URLSearchParams): { removed: boolean; kept: Record<string, string> } {
  const payPalReturn = search.get('provider') === 'paypal' || search.has('PayerID');
  const kept: Record<string, string> = {};
  let removed = false;
  Array.from(new Set(search.keys())).forEach((name) => {
    const value = search.get(name);
    if (!value || !isTaken(name, value, payPalReturn)) return;
    removed = true;
    if (KEPT_PARAMS.has(name)) kept[name] = value;
    search.delete(name);
  });
  if (Object.keys(kept).length > 0) {
    CONTEXT_PARAMS.forEach((name) => {
      const value = search.get(name);
      if (value) kept[name] = value;
    });
  }
  return { removed, kept };
}

/**
 * Moves purchase references out of the address bar (history.replaceState,
 * same path, query and hash otherwise untouched, history.state kept) and
 * stores them for this page. Runs first in src/entry-client.tsx, again on
 * every route change and right before every in-app page view (src/App.tsx),
 * so SDKs only ever see a clean URL.
 */
export function scrubCheckoutReturnParamsFromUrl(): void {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    const { removed, kept } = takeCheckoutReturnRefs(url.searchParams);
    if (!removed) return;
    storeFunnelReturnParams(kept, Date.now(), url.pathname);
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  } catch {
    // Never block the app from starting.
  }
}
